<template>
  <div>
    <b-row>

      <b-col
        cols="12"
      >
        <b-card
          :title="$t('dashboard.my-team.title') + (team.teamDefinition.code ? ': ' + team.teamDefinition.code : '')"
        >

          <b-row>

            <b-col
              cols="12"
              md="6"
            >
              <dl class="row mb-0">
                <dt class="font-bold col-4">
                  {{ $t('dashboard.my-team.label.status') }}
                </dt>
                <dd class="col-8">
                  <b-badge
                    v-if="team.status"
                    variant="light-success"
                  >
                    {{ $t('team.status-switcher.active') }}
                  </b-badge>
                  <b-badge
                    v-else
                    variant="light-danger"
                  >
                    {{ $t('team.status-switcher.inactive') }}
                  </b-badge>
                </dd>
              </dl>

              <dl
                v-if="team.paramedicUser"
                class="row mb-0"
              >
                <dt class="font-bold col-4">
                  {{ $t('dashboard.my-team.label.paramedic-user') }}
                </dt>
                <dd class="col-8">
                  <template>
                    {{ team.paramedicUser.surname }} {{ team.paramedicUser.name }}
                  </template>
                </dd>
              </dl>

              <dl class="row mb-0">
                <dt class="font-bold col-4">
                  {{ $t('dashboard.my-team.label.driver-user') }}
                </dt>
                <dd class="col-8">
                  {{ team.driverUser.surname }} {{ team.driverUser.name }}
                </dd>
              </dl>

            </b-col>

            <b-col
              cols="12"
              md="6"
            >

              <dl class="row mb-0">
                <dt class="font-bold col-4">
                  {{ $t('dashboard.my-team.label.vehicle') }}
                </dt>
                <dd class="col-8">
                  {{ team.vehicle.sideNumber }}
                </dd>
              </dl>

              <dl class="row mb-0">
                <dt class="font-bold col-4">
                  {{ $t('dashboard.my-team.label.working-from') }}
                </dt>
                <dd class="col-8">
                  {{ transformDatetimeToHumanReadablePretty(team.teamStartedAt) }}
                </dd>
              </dl>

              <dl
                v-if="team.currentOrder && team.currentOrder.id"
                class="row mb-0"
              >
                <dt class="font-bold col-4">
                  {{ $t('dashboard.teams.label.current-order') }}
                </dt>
                <dd class="col-8">
                  <router-link :to="{ name: 'order-view', params: { id: team.currentOrder.id } }">
                    {{ team.currentOrder.fullNumber }}
                  </router-link>
                  <span>
                    <br>/&nbsp;{{ $t('order.status.' + team.currentOrder.status) }}
                  </span>
                </dd>
              </dl>

              <dl
                v-if="firstPlannedOrder"
                class="row mb-0"
              >
                <dt class="font-bold col-4">
                  {{ $t('order.planning.planned-title') }}
                </dt>
                <dd class="col-8 d-flex align-items-center flex-wrap">
                  <b-badge
                    variant="light-secondary"
                    class="text-nowrap"
                  >
                    {{ firstPlannedOrder.fullNumber || '-' }}
                  </b-badge>
                  <b-badge
                    v-if="remainingPlannedOrdersCount"
                    variant="light-secondary"
                    class="text-nowrap ml-50"
                  >
                    +{{ remainingPlannedOrdersCount }}
                  </b-badge>
                </dd>
              </dl>

            </b-col>
          </b-row>

        </b-card>
      </b-col>

    </b-row>

  </div>
</template>
<script>
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import { orderRequests } from '@/http/requests/order/order'
import {
  BBadge, BCard, BCol, BRow,
} from 'bootstrap-vue'

const PLANNED_ORDERS_PREVIEW_WINDOW_MS = 12 * 60 * 60 * 1000

export default {
  name: 'TeamStatistics',
  components: {
    BRow,
    BCol,
    BBadge,
    BCard,
  },
  props: {
    team: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadedPlannedOrders: [],
    }
  },
  computed: {
    me() {
      return this.$store.getters['auth/getUser'] || { roles: [] }
    },
    canFetchPlannedOrders() {
      return Array.isArray(this.me.roles)
        && (this.me.roles.includes('ROLE_DISPATCHER') || this.me.roles.includes('ROLE_TEAM_LEADER'))
    },
    plannedOrders() {
      return Array.isArray(this.loadedPlannedOrders) ? this.loadedPlannedOrders : []
    },
    firstPlannedOrder() {
      return this.plannedOrders.length ? this.plannedOrders[0] : null
    },
    remainingPlannedOrdersCount() {
      return this.plannedOrders.length > 1 ? this.plannedOrders.length - 1 : 0
    },
  },
  watch: {
    'team.teamDefinition.id': {
      immediate: true,
      handler() {
        this.fetchPlannedOrders()
      },
    },
  },
  methods: {
    transformDatetimeToHumanReadablePretty,
    async fetchPlannedOrders() {
      if (!this.team?.teamDefinition?.id || !this.canFetchPlannedOrders) {
        this.loadedPlannedOrders = []
        return
      }

      try {
        const response = await orderRequests.getOrders({
          plannedOnly: 1,
          plannedTeamDefinitionId: this.team.teamDefinition.id,
          statuses: ['received'],
          limit: 250,
          page: 1,
          sortField: 'startAt',
          sortDir: 'asc',
        })

        const items = Array.isArray(response?.data?.items) ? response.data.items : []
        this.loadedPlannedOrders = items.filter(this.isOrderWithinPlannedPreviewWindow)
      } catch (error) {
        this.loadedPlannedOrders = []
        parseRequestError(error)
      }
    },
    isOrderWithinPlannedPreviewWindow(order) {
      const referenceDate = this.getPlannedOrderReferenceDate(order)
      if (!referenceDate) {
        return false
      }

      return referenceDate.getTime() <= Date.now() + PLANNED_ORDERS_PREVIEW_WINDOW_MS
    },
    getPlannedOrderReferenceDate(order) {
      const rawValue = order?.startAt
      if (!rawValue) {
        return null
      }

      const parsedDate = new Date(rawValue)
      if (Number.isNaN(parsedDate.getTime())) {
        return null
      }

      return parsedDate
    },
  },
}
</script>
